import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { templateSkeletonStatusOptions } from "constants/v2/appConstants";

import {
  applyTemplateToClips,
  getTemplateById,
  getTemplateMatchAssetsFilter,
  getTemplatesList,
  getTemplatesListGroupByDate,
  getTemplateValidationFields,
} from "./actions";

export const templatesEntityAdapter = createEntityAdapter({
  selectId: (entity) => entity.id,
});

const initialState = templatesEntityAdapter.getInitialState({
  count: 0,
  modals: {},
  skeletons: [],
  templateNotification: true,
  groupByDate: {},
  fromGenerateHighlights: false,
  filters: {},
  templateJobIds: [],
  highlightEditorTemplate: {},
  activeTemplateInfo: {},
  validationFields: [],
  individualApplicationClips: [],
});

export const templatesSlice = createSlice({
  name: "templates",
  initialState: initialState,
  reducers: {
    setTemplateJobIds: (state, action) => {
      if (action.payload.length === 0) {
        state.templateJobIds = [];
      } else {
        state.templateJobIds = [...state.templateJobIds, action.payload];
      }
    },
    removeTemplateJobIds: (state, action) => {
      const idsToRemove = action.payload;

      state.templateJobIds = state.templateJobIds.filter((id) => !idsToRemove.includes(id));
    },
    showTemplatesModal: (state, action) => {
      state.modals.templatesModal = action.payload;
    },
    showCreateTemplatesModal: (state, action) => {
      state.modals.createTemplatesModal = action.payload;
    },
    showTemplateDeleteModal: (state, action) => {
      state.modals.templateDeleteModal = action.payload;
    },
    showTemplateValidationsModal: (state, action) => {
      state.modals.templateValidationsModal = action.payload;
    },
    showTemplatesSkeleton: (state, action) => {
      state.skeletons = action.payload;
    },
    showTemplateErrorMsg: (state, action) => {
      state.skeletons = action.payload;
    },
    showTemplatePreviewModalId: (state, action) => {
      state.modals.templatePreviewModalId = action.payload;
    },
    showTemplateNotification: (state, action) => {
      state.templateNotification = action.payload;
    },
    setFromGenerateHighlights: (state, action) => {
      state.fromGenerateHighlights = action.payload;
    },
    setTemplateValidationFields: (state, action) => {
      state.validationFields = action.payload;
    },
    setHighlightEditorTemplate: (state, action) => {
      state.highlightEditorTemplate = {
        ...state.highlightEditorTemplate,
        ...action.payload,
      };
    },
    resetHighlightEditorTemplate: (state) => {
      state.highlightEditorTemplate = {};
    },
    setActiveTemplateInfo: (state, action) => {
      state.activeTemplateInfo = action.payload;
    },
    addIndividualApplicationClip: (state, action) => {
      state.individualApplicationClips = [...state.individualApplicationClips, action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTemplatesList.fulfilled, (state, action) => {
      if (action.payload.success) {
        if (action.payload?.entities) {
          templatesEntityAdapter.setAll(state, action.payload.entities?.templates || {});
          if (action.payload.result) {
            state.ids = action.payload.result;
          }
          state.count = action.payload.totalLength;
        } else {
          state.ids = [];
          state.count = 0;
          state.entities = {};
        }
      }
    });
    builder.addCase(applyTemplateToClips.fulfilled, (state, action) => {
      const clipId = action?.meta?.arg?.payload?.clipDetails?._id;
      const skeletonId = action?.meta?.arg?.payload?.skeletonId;

      /**
       * attaching projectId to corresponding skeleton, so as to show export progress
       */
      if (action.payload.projectId) {
        let newSkeletonData = state.skeletons || [];
        const newSkeletonArray = newSkeletonData.map((skeleton) => {
          if (skeleton.skeletonId === skeletonId && !skeleton.hasOwnProperty("projectId")) {
            return {
              ...skeleton,
              id: clipId,
              streamId: skeleton.streamId,
              projectId: action.payload.projectId,
              jobId: action.payload.jobId,
              status: templateSkeletonStatusOptions.inProgress,
              exportParams: action.payload?.exportParams,
              reqPayload: action?.meta?.arg?.payload,
            };
          }

          return skeleton;
        });

        state.skeletons = newSkeletonArray;
      } else {
        state.skeletons = state.skeletons.filter((skeleton) => skeleton.skeletonId !== skeletonId);
      }
    });
    builder.addCase(applyTemplateToClips.rejected, (state, action) => {
      const skeletonId = action?.meta?.arg?.payload?.skeletonId;
      let skeletonData = state.skeletons || [];

      const newSkeletonArray = skeletonData.map((skeleton) => {
        if (skeleton.skeletonId === skeletonId) {
          // for timeout error status is 0
          const isTimeout = !action?.payload?.errorCode && !action?.payload?.errStatus;
          if (isTimeout) skeleton.isTimeout = true;
          else {
            skeleton.errorMsg = action?.payload?.errorMessage || "Template Creation Failed!";
            skeleton.reqPayload = action?.meta?.arg?.payload;
            skeleton.status = templateSkeletonStatusOptions.failed;
            skeleton.isQuotaExhausted = action?.payload?.isQuotaExhausted;
            skeleton.quotaEvent = action?.payload?.quotaEvent;
            skeleton.retryData = action?.payload?.retryData;
          }
        }

        return skeleton;
      });

      // clearing out the skeleton for rejection case
      state.skeletons = newSkeletonArray;
      //state.skeletons = state.skeletons.filter((skeleton) => skeleton.id !== clipId);
    });
    builder.addCase(getTemplatesListGroupByDate.fulfilled, (state, action) => {
      if (action.payload.success) {
        if (action.payload?.entities) {
          /**
           * If its a new req with updated filters, them don't append in prev state
           */
          const isNewReq = action?.meta?.arg?.payload?.params?.pageNo === 1;

          if (isNewReq) {
            templatesEntityAdapter.setAll(state, action.payload.entities?.studioTemplates || {});
            state.groupByDate = action.payload.entities?.studioTemplatesGroupByDate || {};
          } else {
            // for infinite scroll pagination
            templatesEntityAdapter.setMany(state, action.payload.entities?.studioTemplates || {});
            state.groupByDate = {
              ...state.groupByDate,
              ...(action.payload.entities?.studioTemplatesGroupByDate || {}),
            };
          }
          state.count = action.payload.totalCount;
        } else {
          state.ids = [];
          state.count = 0;
          state.entities = {};
          state.groupByDate = {};
        }
      }
    });
    builder.addCase(getTemplateMatchAssetsFilter.fulfilled, (state, action) => {
      if (action.payload.success) {
        state.filters = {
          ...state.filters,
          match_assets: action?.payload?.data || [],
        };
      }
    });
    builder.addCase(getTemplateValidationFields.fulfilled, (state, action) => {
      if (action.payload.success) {
        state.validationFields = action?.payload?.data?.fields;
      }
    });
    builder.addCase(getTemplateById.fulfilled, (state, action) => {
      if (action.payload.success) {
        const template = action?.payload?.data;

        state.entities = {
          ...state.entities,
          [template.id]: template,
        };
      }
    });
  },
});

export const {
  showTemplatesModal,
  showTemplatesSkeleton,
  showTemplatePreviewModalId,
  showTemplateNotification,
  showCreateTemplatesModal,
  showTemplateDeleteModal,
  showTemplateValidationsModal,
  setFromGenerateHighlights,
  setTemplateJobIds,
  removeTemplateJobIds,
  setHighlightEditorTemplate,
  resetHighlightEditorTemplate,
  setActiveTemplateInfo,
  setTemplateValidationFields,
  addIndividualApplicationClip,
} = templatesSlice.actions;

export default templatesSlice.reducer;
